import { useEffect, useRef } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

import { toSlug } from "@/utils/slug";

import {
  CAMPAIGN_SLUG_MAX,
  isCampaignSlug,
  SLUG_SUFFIX_MAX,
  SLUG_SUFFIX_START,
  SlugStatus,
  withSlugSuffix,
} from "../constants/campaign-slug";

const NAME_FIELD = "campaignName";
const SLUG_FIELD = "slug";
const USER_CHANGE = "change";
const NO_SUGGESTION = "";

export interface CampaignSlugFormValues {
  campaignName?: string;
  slug?: string;
}

export function suggestSlug(campaignName: string | undefined): string {
  const slug: string = toSlug(campaignName, CAMPAIGN_SLUG_MAX);
  return isCampaignSlug(slug) ? slug : NO_SUGGESTION;
}

export function useCampaignSlugSuggestion<T extends CampaignSlugFormValues>(
  form: UseFormReturn<T>,
  status: SlugStatus,
): void {

  const { watch, getValues, setValue, formState } =
    form as unknown as UseFormReturn<FieldValues>;
  const hasSavedSlug: boolean = Boolean(formState.defaultValues?.[SLUG_FIELD]);
  const nameSuggestion = useRef<string>(suggestSlug(getValues(NAME_FIELD)));
  const written = useRef<string>(NO_SUGGESTION);
  const suffix = useRef<number>(SLUG_SUFFIX_START);

  useEffect(() => {
    const subscription = watch((_values, { name, type }) => {
      const suggestion: string = suggestSlug(getValues(NAME_FIELD));
      const nameChanged: boolean = suggestion !== nameSuggestion.current;
      nameSuggestion.current = suggestion;
      if (hasSavedSlug) return;

      const current: string = getValues(SLUG_FIELD) ?? NO_SUGGESTION;
      const isUntouched: boolean = current === written.current;
      const typedName: boolean = name === NAME_FIELD && type === USER_CHANGE;
      const needsFirstFill: boolean = current === NO_SUGGESTION;
      if (!isUntouched || current === suggestion) return;
      if (!(typedName && nameChanged) && !needsFirstFill) return;

      written.current = suggestion;
      suffix.current = SLUG_SUFFIX_START;
      setValue(SLUG_FIELD, suggestion, { shouldDirty: true });
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues, setValue, hasSavedSlug]);

  useEffect(() => {
    if (status !== SlugStatus.TAKEN || hasSavedSlug) return;
    if (getValues(SLUG_FIELD) !== written.current || !written.current) return;
    if (suffix.current > SLUG_SUFFIX_MAX) return;

    const next: string = withSlugSuffix(nameSuggestion.current, suffix.current);
    suffix.current += 1;
    written.current = next;
    setValue(SLUG_FIELD, next, { shouldDirty: true });
  }, [status, getValues, setValue, hasSavedSlug]);
}
